import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { useAuth } from "@/context/AuthContext";
import { ArrowRight, BookOpen, User, Books, Scissors, PencilLine, CalendarBlank, ChartLineUp } from "@phosphor-icons/react";

export default function Dashboard() {
  const { user } = useAuth();
  const [counts, setCounts] = useState({ kb: 0, stories: 0, swipes: 0, posts: 0 });
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [kb, s, sw, p, sum] = await Promise.all([
          api.get("/kb"), api.get("/stories"), api.get("/swipes"), api.get("/posts"), api.get("/analytics/summary"),
        ]);
        setCounts({ kb: kb.data.length, stories: s.data.length, swipes: sw.data.length, posts: p.data.length });
        setSummary(sum.data);
      } finally { setLoading(false); }
    })();
  }, []);

  const cards = [
    { to: "/app/kb", icon: BookOpen, label: "Knowledge base", value: counts.kb, unit: "sources", testid: "card-kb" },
    { to: "/app/identity", icon: User, label: "About me", value: "→", unit: "identity", testid: "card-identity" },
    { to: "/app/storybank", icon: Books, label: "Storybank", value: counts.stories, unit: "stories", testid: "card-storybank" },
    { to: "/app/swipes", icon: Scissors, label: "Swipe file", value: counts.swipes, unit: "swipes", testid: "card-swipes" },
    { to: "/app/planner", icon: CalendarBlank, label: "Planner", value: counts.posts, unit: "posts", testid: "card-planner" },
    { to: "/app/analytics", icon: ChartLineUp, label: "Analytics", value: summary?.totals?.posts_published ?? 0, unit: "published", testid: "card-analytics" },
  ];

  return (
    <div>
      <PageHeader
        eyebrow={`Good to see you, ${user?.name?.split(" ")[0] || "friend"}`}
        title="Your operating cycle."
        subtitle="Feed her sources and stories, draft with her, ship, measure, the loop compounds every week."
        action={<Link to="/app/draft" className="btn btn-accent" data-testid="cta-new-draft">New draft <PencilLine size={16}/></Link>}
      />

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-12">
        {cards.map((c) => (
          <Link key={c.to} to={c.to} data-testid={c.testid} className="card p-6 hover:border-black transition-colors fade-up">
            <c.icon size={24} weight="duotone" style={{ color: "var(--accent)" }} />
            <div className="mt-6 font-serif text-4xl tracking-tight">{loading ? "-" : c.value}</div>
            <div className="mt-1 eyebrow">{c.label}</div>
            <div className="text-xs mt-1" style={{color:'var(--ink-3)'}}>{c.unit}</div>
          </Link>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="card p-6">
          <div className="eyebrow mb-3">This week</div>
          <h3 className="font-serif text-2xl mb-4">Where to focus</h3>
          <ul className="space-y-3 text-sm">
            <li className="flex justify-between  pb-2">
              <span>Add 3 sources to your knowledge base</span>
              <Link to="/app/kb" className="link-underline">Open →</Link>
            </li>
            <li className="flex justify-between  pb-2">
              <span>Capture 5 raw stories in Storybank</span>
              <Link to="/app/storybank" className="link-underline">Open →</Link>
            </li>
            <li className="flex justify-between  pb-2">
              <span>Paste 10 of your best posts for style training</span>
              <Link to="/app/identity" className="link-underline">Open →</Link>
            </li>
            <li className="flex justify-between">
              <span>Draft this week's post</span>
              <Link to="/app/draft" className="link-underline">Open →</Link>
            </li>
          </ul>
        </div>

        <div className="card p-6 grain">
          <div className="eyebrow mb-3">The mental model</div>
          <p className="font-serif text-2xl leading-snug mb-4">
            "Imagine you hired a brilliant brand manager who understands your personal brand to the T, and never stops learning."
          </p>
          <p className="text-sm" style={{color:'var(--ink-2)'}}>
            Every module here is one organ of that assistant. The Planner is her calendar, the KB is her education, the Storybank her archive, the Swipe file her pattern library. The loop closes when performance flows back into her memory.
          </p>
          <Link to="/app/draft" className="mt-6 inline-flex items-center gap-2 link-underline text-sm">Open Drafting Studio <ArrowRight size={14}/></Link>
        </div>
      </div>
    </div>
  );
}
